/**
 * <pdp-gallery-dots>
 *
 * Sync dot indicators with the inner Dawn <slider-component>. Listens for the
 * `slideChanged` CustomEvent dispatched by SliderComponent.update()
 * (assets/global.js:622) and toggles `aria-current` on the matching dot.
 *
 * Dot <-> slide resolution uses each dot's `aria-controls` attribute (which
 * points at the slide's DOM id), so the mapping survives the `featured_media`
 * reordering performed by pdp-media-gallery.liquid (featured slide is rendered
 * first regardless of its position in product.media). Without this, when a
 * variant's featured image is not product.media[0], dot N would scroll to the
 * wrong slide.
 *
 * Click handling uses target-lock suppression: on a dot (or switch) click we
 * optimistically paint the destination and drop intermediate slideChanged events
 * fired while the smooth-scroll animation passes over interim slides (the cause
 * of the "dot 1 -> dot 4 -> dot 3 -> dot 2 -> dot 1" flicker on long jumps). The
 * lock releases when the slider arrives at the target slide (matched by id).
 * Every interim page re-arms a 1200ms safety timeout, so a long jump is never cut
 * short; if the scroll ends anywhere else (an interrupting swipe), or there was
 * nothing to scroll, the timeout settles the dots, the switch and the clips on
 * wherever the slider is. The jump is instant under prefers-reduced-motion, as
 * the track's own CSS intends (behavior 'instant', so the jump never depends on
 * which stylesheet wins the track's scroll-behavior).
 *
 * Video slide (2026-10-21, the product video round's surface 1):
 *   - Pause on slide change. Dawn's <media-gallery> (media-gallery.js), which
 *     would pause a playing clip when the slide changes, is not loaded by this
 *     theme, so this element does it — scoped to ITS OWN slides' <video>
 *     elements, and never the clip on the slide being arrived at (a jump's
 *     target, else the new current slide). Never window.pauseAllMedia(): on
 *     collection pages the showcase band shares this gallery under an
 *     autoplaying hero reel, which that global call would stop.
 *   - Un-mute on the poster tap. Shopify's `media_tag` forces muted="muted"
 *     whenever autoplay is passed, and product-media.liquid passes autoplay so
 *     the clip starts on the tap (DeferredMedia.loadContent only calls play()
 *     when the autoplay attribute is present). The tap is a user gesture, so
 *     un-muting here is honoured; if a browser policy ever refuses, the clip
 *     keeps playing muted and the native controls expose the speaker. This
 *     listener sits on the element (bubble phase), so it always runs after
 *     Dawn's listener on the poster button, which has already appended the
 *     <video> synchronously.
 *
 * Photos | Video switch (2026-10-23, the Watch Video chip round's option 6; it
 * replaced the "Watch Video" chip):
 *   - Two .pdp-gallery__switch-option buttons (aria-pressed; aria-controls names
 *     the slide each side opens). Video starts the clip INSIDE the tap — Dawn's
 *     DeferredMedia.loadContent() appends the <video> and plays it, then it is
 *     un-muted in the same gesture, the one moment browsers allow sound — and
 *     the gallery then slides to it. Photos slides back to the first photo.
 *   - The switch follows the slider: a slide holding a <deferred-media> is the
 *     Video side, any other the Photos side, whether the slider moved by a
 *     swipe, an arrow or a jump. While the Video side is up the element carries
 *     data-showing="video" and component-pdp-gallery.css hides the dots.
 *
 * No dependencies. Vanilla custom element. Loaded with defer from
 * sections/main-product-pdp.liquid and sections/collection-product-feature.liquid.
 * The whole definition is wrapped in a customElements.get guard so the script is
 * safe to include once per section instance (the Collection page renders several
 * showcases) — a bare top-level `class` declaration would throw "already declared"
 * on the 2nd include before the define() guard could run.
 */
if (!customElements.get('pdp-gallery-dots')) {
  customElements.define('pdp-gallery-dots', class PdpGalleryDots extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('slider-component');
    this.dots = Array.from(this.querySelectorAll('.pdp-gallery__dot'));
    this.switchOptions = Array.from(this.querySelectorAll('.pdp-gallery__switch-option'));
    if (!this.slider || (this.dots.length === 0 && this.switchOptions.length === 0)) return;

    this.targetSlideId = null;
    this.targetTimeout = null;

    this.onSlideChanged = this.onSlideChanged.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onSwitchClick = this.onSwitchClick.bind(this);
    this.onPosterClick = this.onPosterClick.bind(this);

    this.slider.addEventListener('slideChanged', this.onSlideChanged);
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.switchOptions.forEach((option) => option.addEventListener('click', this.onSwitchClick));
    this.addEventListener('click', this.onPosterClick);
  }

  disconnectedCallback() {
    if (this.slider) this.slider.removeEventListener('slideChanged', this.onSlideChanged);
    this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.switchOptions.forEach((option) => option.removeEventListener('click', this.onSwitchClick));
    this.removeEventListener('click', this.onPosterClick);
    if (this.targetTimeout) clearTimeout(this.targetTimeout);
  }

  onSlideChanged(event) {
    const slide = event.detail && event.detail.currentElement;

    if (this.targetSlideId !== null) {
      // A jump is under way: the pages it passes pause every clip but the one it
      // is heading for, and the painted dot and switch side hold until it lands.
      this.pauseVideos(document.getElementById(this.targetSlideId));
      if (slide && slide.id === this.targetSlideId) {
        this.clearTarget();
      } else {
        this.armTarget();
      }
      return;
    }

    // Swipes and arrows: the clips left behind pause, the slide arrived at keeps its own.
    this.pauseVideos(slide);
    if (slide && slide.id) this.syncTo(slide);
  }

  onDotClick(event) {
    this.goTo(document.getElementById(event.currentTarget.getAttribute('aria-controls')));
  }

  onSwitchClick(event) {
    const option = event.currentTarget;
    const slide = document.getElementById(option.getAttribute('aria-controls'));
    if (!slide) return;
    // The clip starts before the gallery moves: the tap is the one moment browsers allow sound.
    if (option.dataset.shelf === 'video') this.startClip(slide);
    this.goTo(slide);
  }

  onPosterClick(event) {
    const poster = event.target.closest('.deferred-media__poster');
    if (!poster || !this.contains(poster)) return;
    const video = poster.parentElement && poster.parentElement.querySelector('video');
    if (video) video.muted = false;
  }

  startClip(slide) {
    const media = slide.querySelector('deferred-media');
    if (!media) return;
    // Dawn appends the <video> and plays it, muted (media_tag's autoplay forces the attribute).
    if (!media.hasAttribute('loaded') && typeof media.loadContent === 'function') media.loadContent(false);
    const video = media.querySelector('video');
    if (!video) return;
    video.muted = false;
    if (video.paused) {
      video.play().catch(() => {
        // A browser that refuses sound still gets the clip, muted; the controls expose the speaker.
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }

  goTo(slide) {
    if (!slide || !this.slider.slider) return;
    this.targetSlideId = slide.id;
    this.syncTo(slide);
    // 'instant', not 'auto': 'auto' follows whichever stylesheet wins the track's scroll-behavior, and on phones
    // Dawn's `.slider.slider--mobile { smooth }` once won it (component-pdp-gallery.css now outranks it).
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slider.slider.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'instant' : 'smooth' });
    this.armTarget();
  }

  armTarget() {
    if (this.targetTimeout) clearTimeout(this.targetTimeout);
    this.targetTimeout = setTimeout(() => this.settle(), 1200);
  }

  settle() {
    this.clearTarget();
    const items = this.slider.sliderItemsToShow;
    const slide = items && this.slider.currentPage ? items[this.slider.currentPage - 1] : null;
    if (!slide) return;
    this.pauseVideos(slide);
    this.syncTo(slide);
  }

  clearTarget() {
    this.targetSlideId = null;
    if (this.targetTimeout) {
      clearTimeout(this.targetTimeout);
      this.targetTimeout = null;
    }
  }

  syncTo(slide) {
    const dotIndex = this.dots.findIndex((dot) => dot.getAttribute('aria-controls') === slide.id);
    if (dotIndex > -1) this.setActive(dotIndex);
    if (this.switchOptions.length === 0) return;

    const shelf = slide.querySelector('deferred-media') ? 'video' : 'photos';
    this.switchOptions.forEach((option) => {
      option.setAttribute('aria-pressed', option.dataset.shelf === shelf ? 'true' : 'false');
    });
    if (shelf === 'video') {
      this.dataset.showing = 'video';
    } else {
      delete this.dataset.showing;
    }
  }

  pauseVideos(keep) {
    this.querySelectorAll('.pdp-gallery__slide video').forEach((video) => {
      if (!keep || !keep.contains(video)) video.pause();
    });
  }

  setActive(index) {
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
  });
}
